import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IcBaselineAutoAwesome from '~icons/ic/baseline-auto-awesome'
import IcBaselineBrush from '~icons/ic/baseline-brush'
import { cn } from 'src/utils/cx'
import {
  ASPECT_RATIO_KEYS,
  LOADING_STEP_COUNT,
  PROMPT_TAG_ICONS,
  PROMPT_TAG_KEYS,
  STYLE_KEYS,
} from '../constants'
import { useNavigateToGenerator } from '../hooks/useNavigateToGenerator'
import css from '../Landing.module.css'
import { useFadeUp } from './useFadeUp'

export default function ExperienceSection() {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const [activeStyle, setActiveStyle] = useState(0)
  const [activeRatio, setActiveRatio] = useState(1) // portrait
  const [prompt, setPrompt] = useState(() =>
    t(`experience.styles.${STYLE_KEYS[0]}.prompt`),
  )
  const [loading, setLoading] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [loadingText, setLoadingText] = useState(() =>
    t('experience.loading-steps.0'),
  )
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  )
  const fadeRef = useFadeUp()
  const navigateToGenerator = useNavigateToGenerator()
  const promptRef = useRef(prompt)
  promptRef.current = prompt

  const handleGenerate = useCallback(() => {
    if (loading) return
    setLoading(true)
    setLoadingProgress(0)
    let progress = 0

    clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      progress += 2 + Math.random() * 3
      if (progress > 100) progress = 100
      setLoadingProgress(progress)
      const stepIdx = Math.min(
        Math.floor(progress / 25),
        LOADING_STEP_COUNT - 1,
      )
      setLoadingText(t(`experience.loading-steps.${stepIdx}`))

      if (progress >= 100) {
        clearInterval(intervalRef.current)
        setTimeout(() => {
          setLoading(false)
          navigateToGenerator(promptRef.current)
        }, 400)
      }
    }, 80)
  }, [loading, navigateToGenerator, t])

  return (
    <>
      <section
        id="experience"
        className="py-24"
        style={{ background: 'var(--landing-pink-bg2)' }}
      >
        <div className="mx-auto max-w-[1200px] px-6">
          <div
            ref={fadeRef}
            className={cn(
              css.fadeUp,
              'mx-auto max-w-[880px] rounded-[28px] border-[1.5px] border-gray-200 bg-white p-8 shadow-sm md:p-12',
            )}
          >
            <h2 className="mb-8 flex items-center justify-center gap-2 text-center text-2xl font-semibold">
              <IcBaselineBrush className="size-7 text-[--landing-pink]" />
              {t('experience.heading')}
            </h2>

            {/* Style chips */}
            <div className="mb-6 flex flex-wrap justify-center gap-2.5">
              {STYLE_KEYS.map((key, i) => (
                <button
                  key={key}
                  onClick={() => {
                    setActiveStyle(i)
                    setPrompt(t(`experience.styles.${key}.prompt`))
                  }}
                  className={cn(
                    'rounded-full border-[1.5px] px-5 py-2.5 text-sm font-medium transition-all',
                    activeStyle === i
                      ? 'border-[--landing-pink] bg-[--landing-pink-bg] text-[--landing-pink-dark]'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-[--landing-pink] hover:bg-[--landing-pink-bg] hover:text-[--landing-pink-dark]',
                  )}
                >
                  {t(`experience.styles.${key}.label`)}
                </button>
              ))}
            </div>

            {/* Prompt textarea */}
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={t('experience.prompt-placeholder')}
              autoComplete="off"
              autoCorrect="off"
              data-form-type="other"
              className="mb-4 min-h-[100px] w-full resize-none rounded-2xl border-[1.5px] border-gray-200 bg-gray-50 p-5 text-base text-gray-800 outline-none transition-colors placeholder:text-gray-400 focus:border-[--landing-pink]"
              style={{ lineHeight: 1.7 }}
            />

            {/* Prompt tags */}
            <div className="mb-6 flex flex-wrap gap-2">
              {PROMPT_TAG_KEYS.map(key => {
                const Icon = PROMPT_TAG_ICONS[key]
                return (
                  <button
                    key={key}
                    onClick={() =>
                      setPrompt(t(`experience.tags.${key}.prompt`))
                    }
                    className="inline-flex items-center gap-1.5 rounded-full bg-[--landing-pink-bg] px-3.5 py-1.5 text-xs font-medium text-[--landing-pink-dark] transition-colors hover:bg-[--landing-pink-light] hover:text-white"
                  >
                    <Icon className="size-3.5" />
                    {t(`experience.tags.${key}.label`)}
                  </button>
                )
              })}
            </div>

            {/* Bottom: ratio + generate */}
            <div className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <span className="mr-1 text-sm text-gray-500">
                  {t('experience.ratio-label')}
                </span>
                {ASPECT_RATIO_KEYS.map((key, i) => (
                  <button
                    key={key}
                    onClick={() => setActiveRatio(i)}
                    className={cn(
                      'rounded-lg border-[1.5px] px-3.5 py-2 text-xs font-medium transition-all',
                      activeRatio === i
                        ? 'border-[--landing-pink] bg-[--landing-pink-bg] text-[--landing-pink-dark]'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-[--landing-pink] hover:bg-[--landing-pink-bg] hover:text-[--landing-pink-dark]',
                    )}
                  >
                    {t(`experience.ratios.${key}`)}
                  </button>
                ))}
              </div>

              <button
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-br from-[#FF83B8] to-[#FF5A9E] px-12 py-4 text-lg font-bold text-white shadow-[0_4px_20px_rgba(255,131,184,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(255,131,184,0.5)]"
              >
                <IcBaselineAutoAwesome className="size-5" />{' '}
                {t('experience.create-button')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 z-[1999] flex flex-col items-center justify-center gap-5 bg-white/92 backdrop-blur-lg">
          <div className="text-base font-semibold text-gray-700">
            {loadingText}
          </div>
          <div className="h-1.5 w-[280px] overflow-hidden rounded-full bg-gray-200">
            <div
              className={cn(css.loadingBar, 'h-full rounded-full')}
              style={{
                width: `${loadingProgress}%`,
                background:
                  'linear-gradient(90deg, var(--landing-pink), var(--landing-pink-dark))',
              }}
            />
          </div>
          <div className="-mt-3 text-xs text-gray-400">
            {t('experience.model-note')}
          </div>
        </div>
      )}
    </>
  )
}
